import dayjs from 'dayjs';
import {
  CONSENT_CHANNELS, CONTRACT_PREFIX, MOVE_TYPE, REJECT_REASONS, SCHEDULES, STAGES, addDays, addMonths, isInterviewStage, probationEndOf, probationLimit, toDay,
  type AssetItem, type Branch, type Candidate, type Dependent, type LeaveRecord, type MoveItem, type RecordItem, type Contract, type Dept, type Education, type Employee, type HrState, type LeaveType, type Offboarding,
  type OffTask, type Position, type ReqReason, type Requisition, type Source, type Stage, type StageStep,
} from './hrLogic';

/** Tăng số này khi đổi cấu trúc dữ liệu demo — trình duyệt đang giữ demo cũ sẽ tự nạp lại */
export const DEMO_VERSION = 14;

const BRANCHES: Branch[] = [
  { id: 'br-office', name: 'Văn phòng điều hành', short: 'Văn phòng', clinic: false },
  { id: 'br-q1', name: 'Chi nhánh Quận 1', short: 'Quận 1', clinic: true },
  { id: 'br-q2', name: 'Chi nhánh Quận 2', short: 'Quận 2', clinic: true },
  { id: 'br-td', name: 'Chi nhánh Thủ Đức', short: 'Thủ Đức', clinic: true },
];

const DEPTS: Dept[] = [
  { id: 'bgd', name: 'Ban giám đốc', color: '#475569' },
  { id: 'doctor', name: 'Bác sĩ', color: '#7c3aed' },
  { id: 'nursing', name: 'Điều dưỡng', color: '#0ea5e9' },
  { id: 'tech', name: 'Kỹ thuật viên', color: '#ec4899' },
  { id: 'consult', name: 'Tư vấn', color: '#f59e0b' },
  { id: 'front', name: 'Lễ tân & CSKH', color: '#14b8a6' },
  { id: 'mkt', name: 'Marketing', color: '#6366f1' },
  { id: 'office', name: 'Kế toán – Kho', color: '#64748b' },
  { id: 'hr', name: 'Nhân sự – Hành chính', color: '#22c55e' },
];

const M = 1_000_000;
const POSITIONS: Position[] = [
  { id: 'p-ceo', name: 'Giám đốc điều hành', deptId: 'bgd', level: 'director', salaryMin: 60 * M, salaryMax: 100 * M },
  { id: 'p-bm', name: 'Giám đốc chi nhánh', deptId: 'bgd', level: 'manager', salaryMin: 28 * M, salaryMax: 45 * M },
  { id: 'p-doc-lead', name: 'Bác sĩ phụ trách chuyên môn', deptId: 'doctor', level: 'manager', license: 'gphn', salaryMin: 50 * M, salaryMax: 80 * M },
  { id: 'p-doc', name: 'Bác sĩ thẩm mỹ', deptId: 'doctor', level: 'staff', license: 'gphn', salaryMin: 30 * M, salaryMax: 50 * M },
  { id: 'p-nurse-lead', name: 'Điều dưỡng trưởng', deptId: 'nursing', level: 'lead', license: 'gphn', salaryMin: 14 * M, salaryMax: 20 * M },
  { id: 'p-nurse', name: 'Điều dưỡng', deptId: 'nursing', level: 'staff', license: 'gphn', salaryMin: 9 * M, salaryMax: 14 * M },
  { id: 'p-tech-lead', name: 'Trưởng nhóm kỹ thuật viên', deptId: 'tech', level: 'lead', license: 'cert', salaryMin: 13 * M, salaryMax: 18 * M },
  { id: 'p-tech', name: 'Kỹ thuật viên spa – laser', deptId: 'tech', level: 'staff', license: 'cert', salaryMin: 7 * M, salaryMax: 12 * M },
  { id: 'p-cons-lead', name: 'Trưởng nhóm tư vấn', deptId: 'consult', level: 'lead', salaryMin: 15 * M, salaryMax: 22 * M },
  { id: 'p-cons', name: 'Tư vấn viên', deptId: 'consult', level: 'staff', salaryMin: 8 * M, salaryMax: 13 * M },
  { id: 'p-front', name: 'Lễ tân', deptId: 'front', level: 'staff', salaryMin: 6.5 * M, salaryMax: 9 * M },
  { id: 'p-cskh', name: 'Nhân viên chăm sóc khách hàng', deptId: 'front', level: 'staff', salaryMin: 7.5 * M, salaryMax: 11 * M },
  { id: 'p-mkt-lead', name: 'Trưởng phòng Marketing', deptId: 'mkt', level: 'manager', salaryMin: 25 * M, salaryMax: 40 * M },
  { id: 'p-mkt', name: 'Chuyên viên Marketing', deptId: 'mkt', level: 'staff', salaryMin: 12 * M, salaryMax: 20 * M },
  { id: 'p-acc-lead', name: 'Kế toán trưởng', deptId: 'office', level: 'manager', salaryMin: 22 * M, salaryMax: 35 * M },
  { id: 'p-acc', name: 'Kế toán viên', deptId: 'office', level: 'staff', salaryMin: 10 * M, salaryMax: 15 * M },
  { id: 'p-wh', name: 'Thủ kho', deptId: 'office', level: 'staff', salaryMin: 8 * M, salaryMax: 11 * M },
  { id: 'p-hr-lead', name: 'Trưởng phòng Nhân sự', deptId: 'hr', level: 'manager', salaryMin: 22 * M, salaryMax: 35 * M },
  { id: 'p-hr', name: 'Chuyên viên tuyển dụng – C&B', deptId: 'hr', level: 'staff', salaryMin: 11 * M, salaryMax: 17 * M },
];

const PLAN: Record<string, Record<string, number>> = {
  'br-office': { bgd: 1, mkt: 6, office: 4, hr: 2 },
  'br-q1': { bgd: 1, doctor: 3, nursing: 5, tech: 7, consult: 6, front: 4 },
  'br-q2': { bgd: 1, doctor: 3, nursing: 4, tech: 6, consult: 5, front: 3 },
  'br-td': { bgd: 1, doctor: 2, nursing: 3, tech: 3, consult: 4, front: 2 },
};

/** Quân số hiện có theo vị trí (chưa tính các ca dựng sẵn bên dưới) */
const ROSTER: [branch: string, pos: string, n: number][] = [
  ['br-office', 'p-ceo', 1], ['br-office', 'p-mkt-lead', 1], ['br-office', 'p-mkt', 2], ['br-office', 'p-acc-lead', 1], ['br-office', 'p-acc', 2],
  ['br-office', 'p-wh', 1], ['br-office', 'p-hr-lead', 1], ['br-office', 'p-hr', 1],
  ['br-q1', 'p-bm', 1], ['br-q1', 'p-doc-lead', 1], ['br-q1', 'p-doc', 2], ['br-q1', 'p-nurse-lead', 1], ['br-q1', 'p-nurse', 2],
  ['br-q1', 'p-tech-lead', 1], ['br-q1', 'p-tech', 3], ['br-q1', 'p-cons-lead', 1], ['br-q1', 'p-cons', 3], ['br-q1', 'p-front', 2], ['br-q1', 'p-cskh', 1],
  ['br-q2', 'p-bm', 1], ['br-q2', 'p-doc-lead', 1], ['br-q2', 'p-doc', 1], ['br-q2', 'p-nurse-lead', 1], ['br-q2', 'p-nurse', 2],
  ['br-q2', 'p-tech-lead', 1], ['br-q2', 'p-tech', 3], ['br-q2', 'p-cons-lead', 1], ['br-q2', 'p-cons', 3], ['br-q2', 'p-front', 2], ['br-q2', 'p-cskh', 1],
];

const EDU: Record<string, Education[]> = {
  bgd: ['university'], doctor: ['university'], nursing: ['college', 'university', 'college'], tech: ['intermediate', 'intermediate', 'other'],
  consult: ['college', 'other', 'intermediate'], front: ['other', 'intermediate'], mkt: ['university'], office: ['university', 'college'], hr: ['university'],
};
const FEMALE_RATE: Record<string, number> = { bgd: 0.5, doctor: 0.5, nursing: 0.85, tech: 0.92, consult: 0.85, front: 0.9, mkt: 0.6, office: 0.7, hr: 0.8 };
const SOURCES: Record<string, Source[]> = {
  doctor: ['referral', 'referral', 'topcv', 'school'], nursing: ['school', 'referral', 'topcv', 'facebook'],
  tech: ['facebook', 'school', 'referral', 'facebook', 'walkin'], consult: ['facebook', 'topcv', 'referral', 'facebook', 'walkin'],
  front: ['facebook', 'topcv', 'walkin', 'referral'], default: ['topcv', 'website', 'referral'],
};

const FAM = ['Nguyễn', 'Trần', 'Lê', 'Phạm', 'Hoàng', 'Huỳnh', 'Phan', 'Vũ', 'Võ', 'Đặng', 'Bùi', 'Đỗ', 'Hồ', 'Ngô', 'Dương', 'Lý', 'Trương', 'Đinh'];
const F_MID = ['Thị', 'Ngọc', 'Thu', 'Mỹ', 'Bảo', 'Thanh', 'Kim', 'Hoài', 'Phương', 'Khánh'];
const F_GIV = ['Anh', 'Trang', 'Linh', 'Hương', 'Ngân', 'Vy', 'Thảo', 'Nhung', 'Phương', 'Duyên', 'Hạnh', 'Yến', 'Trâm', 'Quỳnh', 'My', 'Tiên', 'Hà', 'Lan', 'Uyên', 'Nhi', 'Châu', 'Giang', 'Hằng', 'Loan'];
const M_MID = ['Văn', 'Minh', 'Quốc', 'Đức', 'Hoàng', 'Thành', 'Gia', 'Hữu'];
const M_GIV = ['Huy', 'Khoa', 'Nam', 'Long', 'Phúc', 'Tài', 'Bảo', 'Tuấn', 'Dũng', 'Khang', 'Sơn', 'Hiếu', 'Thịnh', 'Trung'];

/** Bộ sinh số ngẫu nhiên cố định (mulberry32) để dữ liệu demo ổn định giữa các lần nạp */
function rngOf(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const PROVINCES = ['TP. Hồ Chí Minh', 'Đồng Nai', 'Long An', 'Tiền Giang', 'Bến Tre', 'Cần Thơ', 'Bình Định', 'Quảng Ngãi', 'Nghệ An', 'Đắk Lắk', 'Bình Dương', 'Khánh Hòa'];
const STREETS = ['Nguyễn Trãi', 'Lê Văn Sỹ', 'Phan Xích Long', 'Cộng Hòa', 'Nguyễn Văn Linh', 'Võ Văn Ngân', 'Lý Thường Kiệt', 'Trần Não', 'Điện Biên Phủ', 'Hoàng Diệu'];
const WARDS = ['Phường Bến Thành', 'Phường Tân Định', 'Phường An Khánh', 'Phường Thủ Đức', 'Phường Gia Định', 'Phường Bình Trưng', 'Phường Tân Sơn Nhất', 'Phường Chợ Quán'];
const BANKS = ['Vietcombank', 'Techcombank', 'ACB', 'MB Bank', 'VPBank', 'BIDV'];
const PREV_COMPANIES = ['TMV Ánh Dương', 'Spa Mộc An', 'Phòng khám Da liễu Tâm Phúc', 'Viện thẩm mỹ Kim Cương', 'Công ty TNHH Thương mại Minh Long', 'Bệnh viện đa khoa Tâm An'];
const SCHOOLS: Record<string, [school: string, degree: string, major: string][]> = {
  doctor: [['Đại học Y Dược TP.HCM', 'Bác sĩ đa khoa', 'Y đa khoa'], ['Đại học Y khoa Phạm Ngọc Thạch', 'Bác sĩ đa khoa', 'Y đa khoa']],
  nursing: [['Cao đẳng Y tế Pasteur', 'Cao đẳng', 'Điều dưỡng'], ['Đại học Y Dược TP.HCM', 'Cử nhân', 'Điều dưỡng']],
  tech: [['Trung cấp Thẩm mỹ Sài Gòn', 'Trung cấp', 'Chăm sóc da'], ['Trường nghề Thẩm mỹ Á Âu', 'Chứng chỉ nghề', 'Phun xăm thẩm mỹ']],
  consult: [['Cao đẳng Kinh tế TP.HCM', 'Cao đẳng', 'Quản trị kinh doanh'], ['Cao đẳng Bách Việt', 'Cao đẳng', 'Marketing']],
  front: [['Cao đẳng Du lịch Sài Gòn', 'Cao đẳng', 'Quản trị khách sạn'], ['THPT', 'Tốt nghiệp THPT', '']],
  default: [['Đại học Kinh tế TP.HCM', 'Cử nhân', 'Kế toán'], ['Đại học Mở TP.HCM', 'Cử nhân', 'Quản trị nhân lực'], ['Đại học Tôn Đức Thắng', 'Cử nhân', 'Marketing']],
};
const REWARDS: [title: string, note: string][] = [
  ['Nhân viên xuất sắc quý', 'Bình chọn của Ban giám đốc'], ['Top doanh số tư vấn tháng', 'Vượt 135% chỉ tiêu doanh số'],
  ['Khách hàng khen ngợi tận tâm', 'Khách để lại đánh giá 5★ và gửi thư cảm ơn'], ['Sáng kiến cải tiến quy trình', 'Rút ngắn thời gian đón tiếp khách 10 phút'],
  ['Hoàn thành xuất sắc đợt khai trương chi nhánh', 'Hỗ trợ setup chi nhánh Thủ Đức'], ['Không nghỉ phép, không đi trễ cả năm', 'Chuyên cần năm'],
];
const VIOLATIONS: [kind: 'work' | 'asset', title: string, level: string, note?: string, amount?: number][] = [
  ['work', 'Đi trễ 4 lần trong tháng', 'Nhắc nhở', 'Đã cam kết đúng giờ'], ['work', 'Không tuân thủ quy trình vô khuẩn khi làm thủ thuật', 'Khiển trách', 'Đào tạo lại quy trình vô khuẩn 1 buổi'],
  ['asset', 'Làm mất thẻ nhân viên', 'Nhắc nhở', 'Bồi thường làm lại thẻ', 50000], ['work', 'Tự ý đổi ca không báo quản lý', 'Nhắc nhở'],
  ['asset', 'Làm rơi vỡ đầu máy laser', 'Khiển trách', 'Bồi thường 20% giá trị sửa chữa', 1500000], ['work', 'Tư vấn sai giá dịch vụ cho khách', 'Nhắc nhở', 'Đã xin lỗi và hoàn chênh lệch cho khách'],
];
const FAM_DEP = ['Nguyễn', 'Trần', 'Lê', 'Phạm'];
/** Vị trí trưởng nhóm / quản lý ← vị trí nhân viên trước khi được bổ nhiệm */
const LEAD_FROM: Record<string, string> = {
  'p-doc-lead': 'p-doc', 'p-nurse-lead': 'p-nurse', 'p-tech-lead': 'p-tech', 'p-cons-lead': 'p-cons', 'p-mkt-lead': 'p-mkt', 'p-acc-lead': 'p-acc', 'p-hr-lead': 'p-hr',
};
const NICKNAMES =['Bông', 'Su', 'Mia', 'Bin', 'Tí', 'Na', 'Kem', 'Bơ', 'Sóc', 'Mít'];
/** Chứng chỉ bổ sung (không bắt buộc) theo bộ phận */
const EXTRA_CERTS: Record<string, [name: string, scope: string][]> = {
  mkt: [['Google Ads Search Certification', 'Quảng cáo tìm kiếm'], ['Meta Certified Digital Marketing Associate', 'Quảng cáo Facebook / Instagram']],
  office: [['Chứng chỉ Kế toán trưởng', 'Kế toán doanh nghiệp'], ['Chứng chỉ Thủ kho dược', 'Bảo quản thuốc, vật tư y tế']],
  hr: [['Chứng chỉ Quản trị nhân sự', 'C&B, tuyển dụng'], ['Chứng chỉ An toàn vệ sinh lao động', 'Nhóm 1']],
  consult: [['Chứng chỉ Tư vấn thẩm mỹ', 'Tư vấn dịch vụ thẩm mỹ không xâm lấn']],
  front: [['Chứng chỉ Chăm sóc khách hàng chuyên nghiệp', 'Lễ tân – CSKH']],
};

/** Hồ sơ mở rộng: thuế, BHXH, giấy tờ, ngân hàng, học vấn, kinh nghiệm, tài sản cấp phát, khen thưởng / vi phạm, lịch sử */
function enrichProfiles(employees: Employee[], contracts: Contract[], ctx: { today: string; hrName: string; rng: () => number }) {
  const R = ctx.rng;
  const ri = (a: number, b: number) => a + Math.floor(R() * (b - a + 1));
  const pick = <T,>(xs: T[]) => xs[Math.floor(R() * xs.length)];
  const D = (n: number) => addDays(ctx.today, n);
  const digits = (n: number) => Array.from({ length: n }, () => ri(0, 9)).join('');
  const slug = (x: string) => x.replace(/đ/gi, 'd').normalize('NFD').replace(/[^a-zA-Z]/g, '').toLowerCase();
  const ts = (d: string, h = 9) => dayjs(d).hour(h).minute(ri(0, 59)).valueOf();
  let seq = 0;
  const code = (p: string) => { seq += 1; return `${p}-${String(seq).padStart(4, '0')}`; };
  employees.forEach((e, idx) => {
    const p = POSITIONS.find(x => x.id === e.positionId)!;
    const dept = p.deptId;
    // Hồ sơ đầu tiên dùng để trình diễn: điền đầy đủ mọi mục
    const showcase = idx === 0 || e.id === 'demo-e1';
    const age = e.dob ? dayjs(ctx.today).diff(dayjs(e.dob), 'year') : 30;
    const left = e.leftDate && e.leftDate < ctx.today ? e.leftDate : undefined;
    const lastName = e.name.split(' ').pop() || '';
    const first = e.name.split(' ')[0];
    e.marital = age > 30 && R() < 0.75 ? 'married' : R() < 0.08 ? 'divorced' : 'single';
    e.dependents = e.marital === 'married' ? ri(0, 2) : 0;
    e.schedule = ['mkt', 'office', 'hr', 'bgd'].includes(dept) ? SCHEDULES[0] : dept === 'doctor' ? pick([SCHEDULES[0], SCHEDULES[2]]) : SCHEDULES[1];
    if (R() < 0.9 || e.hireDate < D(-60)) e.taxCode = `8${digits(9)}`;
    if (e.ins.increasedAt) { e.insuranceNo = `79${digits(8)}`; e.insurancePlace = pick(['BHXH TP. Thủ Đức', 'BHXH Quận 1', 'BHXH Quận Bình Thạnh']); }
    e.cccdDate = D(-ri(400, 2500));
    e.cccdPlace = 'Cục Cảnh sát QLHC về TTXH';
    e.address = `${ri(2, 280)} ${pick(STREETS)}, ${pick(WARDS)}, TP. Hồ Chí Minh`;
    e.birthPlace = pick(PROVINCES);
    e.permanentAddress = R() < 0.5 ? e.address : `${pick(['Xã', 'Phường'])} ${pick(['An Bình', 'Tân Phú', 'Hòa Thành', 'Phước Long'])}, ${e.birthPlace}`;
    e.nationality = 'Việt Nam';
    e.emergency = { name: `${first} ${pick(['Văn', 'Thị'])} ${pick(['Hùng', 'Lan', 'Minh', 'Hoa', 'Tâm'])}`, relation: e.marital === 'married' ? (e.gender === 'Nữ' ? 'Chồng' : 'Vợ') : pick(['Mẹ', 'Bố', 'Anh trai', 'Chị gái']), phone: `09${digits(8)}` };
    e.bank = { account: digits(ri(10, 13)), bank: pick(BANKS), branch: pick(['CN Sài Gòn', 'CN Thủ Đức', 'CN Tân Định', 'CN Bến Thành']) };
    e.email = `${slug(lastName)}.${slug(first)}${e.code.slice(-3)}@thammy-demo.vn`;
    if (showcase || R() < 0.45) e.nickname = pick(NICKNAMES);
    if (showcase || R() < 0.6) e.otherEmail = `${slug(lastName)}${slug(first)}${ri(90, 99)}@gmail.com`;
    if (showcase || R() < 0.4) e.otherPhone = `0${pick(['3', '7', '8'])}${digits(8)}`;
    if (showcase || R() < 0.55) e.facebook = `facebook.com/${slug(lastName)}.${slug(first)}${ri(1, 99)}`;
    // Phép năm đã nghỉ trong năm (tới tháng hiện tại) và phép năm trước chuyển sang
    const monthsThisYear = dayjs(ctx.today).month() + 1 - (e.hireDate.slice(0, 4) === ctx.today.slice(0, 4) ? dayjs(e.hireDate).month() : 0);
    const usedTarget = left ? 0 : Math.max(0, Math.min(12, ri(0, monthsThisYear + 1)));
    e.leaveCarried = !left && e.hireDate < `${ctx.today.slice(0, 4)}-01-01` && R() < 0.4 ? ri(1, 4) : undefined;
    const target = showcase ? 7 : usedTarget;
    if (showcase) e.leaveCarried = 2;
    // Rải các đợt nghỉ phép năm 1–3 ngày trong năm (trước hôm nay, sau ngày vào làm)
    const yStart = e.hireDate > `${ctx.today.slice(0, 4)}-01-01` ? e.hireDate : `${ctx.today.slice(0, 4)}-01-01`;
    const span0 = Math.max(0, dayjs(ctx.today).diff(dayjs(yStart), 'day') - 3);
    const leaves: LeaveRecord[] = [];
    let remainDays = target;
    let guard = 0;
    while (remainDays > 0 && span0 > 10 && guard < 30) {
      guard += 1;
      const len = Math.min(remainDays, ri(1, 3));
      let from = addDays(yStart, ri(0, span0));
      if (dayjs(from).day() === 0) from = addDays(from, 1);
      let to = from; let n = 1;
      while (n < len) { to = addDays(to, 1); if (dayjs(to).day() !== 0) n += 1; }
      if (to >= ctx.today || leaves.some(x => x.from <= to && from <= x.to)) continue;
      leaves.push({ id: `lv-${e.id}-${guard}`, kind: 'annual', from, to, days: len, reason: pick(['Việc gia đình', 'Du lịch', 'Về quê', 'Khám sức khỏe', undefined]), by: ctx.hrName, at: ts(to, 17) });
      remainDays -= len;
    }
    if (!left && R() < 0.15 && span0 > 20) {
      const from = addDays(yStart, ri(0, span0 - 5));
      if (!leaves.some(x => x.from <= addDays(from, 2) && from <= x.to) && dayjs(from).day() !== 0) leaves.push({ id: `lv-${e.id}-s`, kind: 'sick', from, to: addDays(from, 1), days: 2, reason: 'Sốt siêu vi', by: ctx.hrName, at: ts(from, 17) });
    }
    e.leaveRecords = leaves.sort((a, b) => a.from.localeCompare(b.from));
    if (showcase) { e.note ='Phụ trách nội dung TikTok & lịch đăng bài cho 3 chi nhánh'; e.healthCheck = D(-ri(40, 120)); }

    /* Học vấn */
    const sc = SCHOOLS[dept] || SCHOOLS.default;
    const [school, degree, major] = dept === 'doctor' ? sc[ri(0, sc.length - 1)] : pick(sc);
    const gradYear = Math.min(dayjs(e.hireDate).year(), dayjs(e.dob || D(-30 * 365)).year() + (degree.startsWith('Bác sĩ') ? 24 : 21));
    e.schools = [{ id: `sc-${e.id}-1`, school, place: 'TP. Hồ Chí Minh', from: String(gradYear - (degree.startsWith('Bác sĩ') ? 6 : 3)), to: String(gradYear), degree, major, highest: true }];
    if (dept === 'doctor') {
      e.schools[0].highest = false;
      e.schools.push({ id: `sc-${e.id}-2`, school: 'Bệnh viện Da liễu TP.HCM', place: 'TP. Hồ Chí Minh', from: String(gradYear + 2), to: String(gradYear + 3), degree: 'Chứng chỉ chuyên khoa', major: 'Thẩm mỹ da – laser', highest: true });
    }
    if (showcase || (['mkt', 'office', 'hr'].includes(dept) && R() < 0.35)) {
      e.schools.push({ id: `sc-${e.id}-3`, school: dept === 'mkt' ? 'Học viện Digital Marketing VietMoz' : 'Trung tâm đào tạo PTI', place: 'TP. Hồ Chí Minh', from: String(Math.min(dayjs(ctx.today).year(), gradYear + 2)), to: String(Math.min(dayjs(ctx.today).year(), gradYear + 2)), degree: 'Khóa ngắn hạn', major: dept === 'mkt' ? 'Performance Marketing' : 'Kỹ năng quản lý', highest: false });
    }
    if (['tech', 'nursing'].includes(dept) && R() < 0.5) {
      e.schools.push({ id: `sc-${e.id}-3`, school: 'Viện Đào tạo Thẩm mỹ Quốc tế', place: 'TP. Hồ Chí Minh', from: String(gradYear + 1), to: String(gradYear + 1), degree: 'Khóa ngắn hạn', major: pick(['Vận hành máy laser Pico', 'Chăm sóc da sau điều trị', 'Triệt lông Diode']), highest: false });
    }

    /* Kinh nghiệm trước đây */
    const prevYears = Math.max(0, dayjs(e.hireDate).year() - gradYear);
    const nWork = showcase ? 2 : prevYears >= 2 ? Math.min(3, ri(1, Math.ceil(prevYears / 2))) : prevYears === 1 ? 1 : 0;
    let cursor = dayjs(e.hireDate).year();
    e.workHistory = Array.from({ length: nWork }, (_, i) => {
      const to = cursor;
      const from = Math.max(gradYear, to - ri(1, 3));
      cursor = from;
      const role = dept === 'doctor' ? 'Bác sĩ điều trị' : dept === 'mkt' ? pick(['Content Marketing', 'Chuyên viên quảng cáo', 'Marketing Executive']) : p.name.replace('Trưởng nhóm', '').trim() || p.name;
      return {
        id: `wh-${e.id}-${i}`, company: pick(PREV_COMPANIES), position: i === 0 ? role : `${role} (thực tập / junior)`, from: String(from), to: String(to),
        note: i === 0 ? pick(['Nghỉ để phát triển chuyên môn', 'Chuyển nơi ở', 'Muốn môi trường chuyên nghiệp hơn', 'Công ty thu hẹp hoạt động']) : pick(['Phụ trách fanpage và chụp ảnh khách', 'Hỗ trợ chăm sóc khách hàng sau dịch vụ', undefined]),
      };
    });

    /* Chứng chỉ không bắt buộc */
    const certs = EXTRA_CERTS[dept];
    if (certs && (showcase || R() < 0.45)) {
      const [name, scope] = pick(certs);
      e.licenses.push({ id: `lic-${e.id}-x`, kind: 'cert', number: `${name} · ${digits(6)}`, issuedAt: addDays(e.hireDate, -ri(30, 400)), expiresAt: name.startsWith('Google') || name.startsWith('Meta') ? D(showcase ? ri(150, 500) : ri(-200, 600)) : undefined, scope });
    }

    /* Tài sản cấp phát */
    const assets: AssetItem[] = [{ id: `as-${e.id}-card`, kind: 'card', name: 'Thẻ nhân viên + thẻ ra vào', code: code('TNV'), issuedAt: e.hireDate, condition: 'Tốt', value: 50000 }];
    if (['doctor', 'nursing', 'tech', 'consult', 'front'].includes(dept)) assets.push({ id: `as-${e.id}-uni`, kind: 'uniform', name: dept === 'doctor' || dept === 'nursing' ? 'Đồng phục y tế (2 bộ)' : 'Đồng phục chi nhánh (2 bộ)', code: code('DP'), issuedAt: e.hireDate, condition: 'Tốt', value: 600000 });
    if (['office', 'mkt', 'hr', 'bgd'].includes(dept)) assets.push({ id: `as-${e.id}-lt`, kind: 'device', name: pick(['Laptop Dell Latitude 5440', 'Laptop HP ProBook 450', 'MacBook Air M2']), code: code('IT-LT'), issuedAt: addDays(e.hireDate, ri(0, 7)), condition: pick(['Tốt', 'Tốt', 'Trầy xước nhẹ']), value: 18000000, note: 'Kèm sạc, chuột không dây' });
    if (dept === 'doctor' || dept === 'tech') assets.push({ id: `as-${e.id}-tool`, kind: 'tool', name: dept === 'doctor' ? 'Bộ dụng cụ thủ thuật cá nhân' : 'Bộ dụng cụ chăm sóc da', code: code('DC'), issuedAt: addDays(e.hireDate, ri(0, 10)), condition: 'Tốt', value: 3500000 });
    if (dept === 'consult' && R() < 0.6) assets.push({ id: `as-${e.id}-tab`, kind: 'device', name: 'iPad tư vấn (kèm bao da)', code: code('IT-TB'), issuedAt: addDays(e.hireDate, ri(5, 30)), condition: pick(['Tốt', 'Trầy xước nhẹ']), value: 9000000 });
    if (p.level !== 'staff' || showcase) assets.push({ id: `as-${e.id}-sim`, kind: 'device', name: 'SIM công ty + điện thoại Samsung A55', code: code('IT-DT'), issuedAt: addDays(e.hireDate, ri(10, 60)), condition: 'Tốt', value: 8500000, note: `Số máy 09${digits(8)}` });
    if (['front', 'consult', 'tech'].includes(dept) && R() < 0.5) assets.push({ id: `as-${e.id}-locker`, kind: 'card', name: 'Chìa khóa tủ cá nhân', code: code('TU'), issuedAt: e.hireDate, condition: 'Tốt', value: 30000 });
    // Món cũ đã trả lại khi đổi thiết bị
    if (showcase) assets.push({ id: `as-${e.id}-old`, kind: 'device', name: 'Laptop Asus Vivobook (cũ)', code: code('IT-LT'), issuedAt: e.hireDate, condition: 'Hư hỏng nhẹ', returnedAt: addDays(e.hireDate, 400), value: 11000000, note: 'Đổi sang máy mới do pin chai' });
    e.assets = assets.map(a => (left && !a.returnedAt ? { ...a, returnedAt: left } : a));

    /* Khen thưởng / vi phạm */
    const records: RecordItem[] = [];
    const span = Math.max(30, dayjs(left || ctx.today).diff(dayjs(e.hireDate), 'day'));
    const nViol = showcase ? 2 : R() < 0.3 ? (R() < 0.25 ? 2 : 1) : 0;
    for (let i = 0; i < nViol; i += 1) {
      const [kind, title, level, note, amount] = showcase ? VIOLATIONS[i === 0 ? 0 : 2] : pick(VIOLATIONS);
      records.push({ id: `rc-${e.id}-v${i}`, kind, title, level, note, amount, date: addDays(e.hireDate, ri(10, span - 1)), by: ctx.hrName });
    }
    const nRew = span < 120 ? 0 : showcase ? 3 : R() < 0.45 ? (R() < 0.3 ? 2 : 1) : 0;
    for (let i = 0; i < nRew; i += 1) {
      const [title, note] = showcase ? REWARDS[[0, 2, 3][i]] : pick(REWARDS.filter(r => dept === 'consult' || !r[0].includes('doanh số')));
      records.push({ id: `rc-${e.id}-r${i}`, kind: 'reward', title, note, date: addDays(e.hireDate, ri(90, span - 1)), amount: pick([500000, 1000000, 2000000, 3000000]), by: i % 2 ? 'Ban giám đốc' : ctx.hrName });
    }
    if (e.leaveType === 'discipline') records.push({ id: `rc-${e.id}-d`, kind: 'work', title: 'Thu tiền khách không xuất phiếu thu', level: 'Sa thải', date: addDays(e.leftDate!, -6), by: ctx.hrName, note: 'Đã họp xử lý kỷ luật có công đoàn tham dự' });
    e.records = records;

    /* Người phụ thuộc */
    const deps: Dependent[] = [];
    for (let i = 0; i < (e.dependents || 0); i += 1) {
      const dob = addDays(e.hireDate, -ri(-700, 2500));
      if (dob > ctx.today) continue;
      deps.push({ id: `dp-${e.id}-${i}`, name: `${R() < 0.5 ? first : pick(FAM_DEP)} ${pick(['Gia', 'Bảo', 'Minh', 'Ngọc'])} ${pick(['An', 'Khôi', 'Như', 'Hân', 'Phúc'])}`, relation: 'Con', dob, taxCode: R() < 0.6 ? `8${digits(9)}` : undefined, from: (dob > e.hireDate ? dob : e.hireDate).slice(0, 7) });
    }
    if (showcase) deps.push({ id: `dp-${e.id}-p`, name: `${first} Văn Bình`, relation: 'Bố / mẹ', dob: '1958-03-12', taxCode: `8${digits(9)}`, from: '2023-01' });
    e.dependentList = deps;
    e.dependents = deps.length;

    /* Quyết định: bổ nhiệm (trưởng nhóm / quản lý đi lên từ nhân viên), điều chỉnh lương */
    const mine = contracts.filter(c => c.empId === e.id).sort((a, b) => a.start.localeCompare(b.start));
    const payAt = (d: string) => mine.filter(c => c.start <= d).pop()?.salary || 0;
    const moves: MoveItem[] = [];
    const fromStaff = LEAD_FROM[e.positionId];
    if (fromStaff && dayjs(left || ctx.today).diff(dayjs(e.hireDate), 'day') > 700) {
      const date = addDays(e.hireDate, ri(420, 640));
      const prev = payAt(date);
      moves.push({ id: `mv-${e.id}-p`, decisionNo: `QĐ${dayjs(date).format('YYMM')}-${String(ri(1, 40)).padStart(3, '0')}`, type: 'promotion', date, fromPositionId: fromStaff, toPositionId: e.positionId, fromBranchId: e.branchId, toBranchId: e.branchId, prevSalary: prev, salary: Math.round((prev * 1.25) / 500000) * 500000, reason: 'Hoàn thành tốt nhiệm vụ, có năng lực quản lý nhóm', by: 'Ban giám đốc', applied: true });
    }
    if (showcase) {
      const date = D(-ri(150, 200));
      const prev = payAt(date);
      moves.push({ id: `mv-${e.id}-s`, decisionNo: `QĐ${dayjs(date).format('YYMM')}-${String(ri(1, 40)).padStart(3, '0')}`, type: 'salary', date, fromPositionId: e.positionId, toPositionId: e.positionId, fromBranchId: e.branchId, toBranchId: e.branchId, prevSalary: prev, salary: prev + 1500000, reason: 'Đánh giá năm 2025 xếp loại A', by: 'Ban giám đốc', applied: true });
    }
    // Đợt xét tăng lương định kỳ: phần lớn nhân sự chính thức lâu năm đã được điều chỉnh trong 12 tháng qua
    const lastC = mine.filter(c => c.start <= ctx.today).pop();
    if (!showcase && !left && lastC && lastC.kind !== 'probation' && lastC.kind !== 'service' && lastC.start < D(-365) && !moves.some(m => m.date > D(-365)) && R() < 0.7) {
      const date = D(-ri(20, 330));
      const prev = payAt(date);
      moves.push({ id: `mv-${e.id}-r`, decisionNo: `QĐ${dayjs(date).format('YYMM')}-${String(ri(40, 90)).padStart(3, '0')}`, type: 'salary', date, fromPositionId: e.positionId, toPositionId: e.positionId, fromBranchId: e.branchId, toBranchId: e.branchId, prevSalary: prev, salary: Math.round((prev * (1.05 + R() * 0.07)) / 100000) * 100000, reason: pick(['Xét tăng lương định kỳ năm', 'Hoàn thành tốt KPI năm', 'Điều chỉnh theo mặt bằng thị trường']), by: 'Ban giám đốc', applied: true });
    }
    e.moves = moves;

    /* Lịch sử hồ sơ dựng từ các sự kiện */
    const log = [...e.log];
    const add = (d: string | undefined, text: string, by = ctx.hrName, h = 10) => { if (d && d <= ctx.today) log.push({ at: ts(d, h), by, text }); };
    mine.forEach(c => {
      add(c.signedAt, `Ký ${c.kind === 'probation' ? 'thỏa thuận thử việc' : 'hợp đồng'} ${c.code}`, ctx.hrName, 11);
      if (c.evaluation) log.push({ at: c.evaluation.at, by: c.evaluation.by, text: `Kết quả thử việc: ${c.evaluation.result === 'pass' ? 'Đạt' : 'Không đạt'}${c.evaluation.score ? ` (${c.evaluation.score}/10)` : ''}` });
    });
    const vn = (x?: string) => (x ? dayjs(x).format('DD/MM/YYYY') : '');
    add(e.ins.increasedAt, `Báo tăng BHXH ngày ${vn(e.ins.increasedAt)}`);
    add(e.ins.decreasedAt, `Báo giảm BHXH ngày ${vn(e.ins.decreasedAt)}`);
    e.assets.forEach(a => { add(a.issuedAt, `Cấp phát ${a.name} (${a.code})`, ctx.hrName, 14); add(a.returnedAt, `Thu hồi ${a.name}`, ctx.hrName, 16); });
    records.forEach(r => add(r.date, r.kind === 'reward' ? `Khen thưởng: ${r.title}` : `Ghi nhận vi phạm: ${r.title} (${r.level})`, r.by, 15));
    add(e.healthCheck, 'Cập nhật kết quả khám sức khỏe', ctx.hrName, 9);
    moves.forEach(m => add(m.date, `Quyết định ${m.decisionNo}: ${MOVE_TYPE[m.type].label}`, m.by, 8));
    deps.forEach(d => add(`${d.from}-01`, `Đăng ký người phụ thuộc: ${d.name} (${d.relation})`, ctx.hrName, 10));
    add(e.paused?.from, `Tạm hoãn HĐ: ${e.paused?.reason}`);
    add(e.leftDate, `Ngày làm việc cuối: ${e.leaveReason || ''}`);
    e.log = log.sort((a, b) => a.at - b.at);
  });
}

interface Spec {
  branchId: string; positionId: string; hire: string; tag?: string;
  edu?: Education; source?: Source; ctv?: boolean; unsigned?: boolean;
  /** Chưa đánh giá thử việc dù đã hết hạn */
  noEval?: boolean;
  left?: { date: string; type: LeaveType; reason: string; noRehire?: boolean };
}

const DAY = 86400000;

export function demoState(now = Date.now(), by = 'Phòng Nhân sự'): HrState {
  const R = rngOf(20261014);
  const ri = (a: number, b: number) => a + Math.floor(R() * (b - a + 1));
  const pick = <T,>(xs: T[]) => xs[Math.floor(R() * xs.length)];
  const today = toDay(now);
  const D = (n: number) => addDays(today, n);
  const at = (d: string, h = 9) => Math.min(now - 60000, dayjs(d).hour(h).minute(ri(0, 59)).valueOf());
  const pos = (id: string) => POSITIONS.find(p => p.id === id)!;
  const deptOfPos = (id: string) => pos(id).deptId;

  const usedNames = new Set<string>();
  const usedPhones = new Set<string>();
  const makeName = (g: 'Nam' | 'Nữ') => {
    for (;;) {
      const n = `${pick(FAM)} ${g === 'Nữ' ? pick(F_MID) : pick(M_MID)} ${g === 'Nữ' ? pick(F_GIV) : pick(M_GIV)}`;
      if (!usedNames.has(n)) { usedNames.add(n); return n; }
    }
  };
  const makePhone = () => {
    for (;;) {
      const p = `${pick(['090', '093', '097', '098', '070', '077', '085', '036'])}${ri(1000000, 9999999)}`;
      if (!usedPhones.has(p)) { usedPhones.add(p); return p; }
    }
  };
  const roundSalary = (n: number) => Math.round(n / 500000) * 500000;
  const bandSalary = (p: Position) => roundSalary(p.salaryMin + R() * (p.salaryMax - p.salaryMin) * 0.7);

  /* ---------- Danh sách nhân sự (hiện tại + đã nghỉ) ---------- */
  const specs: Spec[] = [];
  ROSTER.forEach(([branchId, positionId, n]) => {
    const lead = pos(positionId).level !== 'staff';
    for (let i = 0; i < n; i += 1) {
      const range = branchId === 'br-q2' ? [-1080, -160] : branchId === 'br-office' ? [-2200, -420] : [-2100, -160];
      specs.push({ branchId, positionId, hire: D(lead ? ri(range[0], Math.max(range[0] + 200, -700)) : ri(range[0], range[1])) });
    }
  });
  // Chi nhánh Thủ Đức khai trương ~150 ngày trước
  [['p-bm', 1], ['p-doc', 1], ['p-nurse', 2], ['p-tech', 2], ['p-cons', 3], ['p-front', 2]].forEach(([p, n]) => {
    for (let i = 0; i < Number(n); i += 1) specs.push({ branchId: 'br-td', positionId: String(p), hire: D(-ri(140, 152)), tag: p === 'p-cons' && i === 0 ? 'td-late-ins' : 'td-open' });
  });
  // Các ca nghiệp vụ dựng sẵn để dashboard có việc cần xử lý
  specs.push(
    { branchId: 'br-q1', positionId: 'p-cons', hire: D(-20), tag: 'q1-cons-prob', edu: 'college', source: 'referral' },
    { branchId: 'br-q1', positionId: 'p-tech', hire: D(-27), tag: 'q1-tech-prob', edu: 'intermediate', source: 'school' },
    { branchId: 'br-q2', positionId: 'p-nurse', hire: D(-62), tag: 'q2-nurse-prob', edu: 'college', source: 'topcv', noEval: true },
    { branchId: 'br-td', positionId: 'p-tech', hire: D(-40), tag: 'td-tech-pass', edu: 'intermediate', source: 'facebook' },
    { branchId: 'br-q2', positionId: 'p-cskh', hire: D(-15), tag: 'q2-cskh-unsigned', edu: 'college', source: 'facebook', unsigned: true },
    { branchId: 'br-office', positionId: 'p-mkt', hire: D(-45), tag: 'off-mkt-prob', edu: 'university', source: 'topcv' },
    { branchId: 'br-q1', positionId: 'p-cons', hire: D(-1100), tag: 'q1-cons-leaving', edu: 'college' },
    { branchId: 'br-q1', positionId: 'p-cskh', hire: D(-400), tag: 'q1-cskh-end', edu: 'intermediate' },
    { branchId: 'br-q1', positionId: 'p-nurse', hire: D(-1125), tag: 'q1-nurse-2fixed', edu: 'college' },
    { branchId: 'br-q1', positionId: 'p-nurse', hire: D(-900), tag: 'q1-nurse-paused', edu: 'college' },
    { branchId: 'br-q2', positionId: 'p-tech', hire: D(-403), tag: 'q2-tech-expired', edu: 'intermediate' },
    { branchId: 'br-q1', positionId: 'p-tech', hire: D(-450), tag: 'q1-tech-auto', edu: 'intermediate' },
    { branchId: 'br-q2', positionId: 'p-cons', hire: D(-700), tag: 'q2-cons-expiring', edu: 'college' },
    { branchId: 'br-office', positionId: 'p-mkt', hire: D(-260), tag: 'off-mkt-recent', edu: 'university', source: 'website' },
    { branchId: 'br-q1', positionId: 'p-tech', hire: D(-200), tag: 'ctv', ctv: true, edu: 'intermediate' },
    { branchId: 'br-q1', positionId: 'p-cons', hire: D(-90), tag: 'ctv', ctv: true, edu: 'other' },
  );
  // Người đã nghỉ (12–20 tháng qua)
  const LEAVERS: [daysAgo: number, branch: string, p: string, type: LeaveType, reason: string, tenure: number, source?: Source, noRehire?: boolean, tag?: string][] = [
    [35, 'br-td', 'p-doc-lead', 'voluntary', 'Có cơ hội tốt hơn', 115, 'referral', false, 'td-lead-left'],
    [20, 'br-q1', 'p-front', 'discipline', 'Vi phạm kỷ luật', 400, 'walkin', true, 'off-open-ins'],
    [6, 'br-q2', 'p-tech', 'voluntary', 'Thu nhập chưa phù hợp', 500, 'facebook', false, 'off-open-settle'],
    [55, 'br-q1', 'p-cons', 'voluntary', 'Áp lực doanh số', 70, 'facebook'],
    [70, 'br-q1', 'p-cons', 'voluntary', 'Có cơ hội tốt hơn', 800, 'topcv'],
    [95, 'br-q2', 'p-cons', 'probation_fail', 'Không đạt thử việc', 50, 'facebook'],
    [120, 'br-q1', 'p-tech', 'voluntary', 'Chuyển nơi ở', 1000, 'school'],
    [150, 'br-q2', 'p-nurse', 'voluntary', 'Thu nhập chưa phù hợp', 600, 'school'],
    [180, 'br-office', 'p-mkt', 'voluntary', 'Có cơ hội tốt hơn', 700, 'topcv'],
    [200, 'br-q1', 'p-cons', 'dismissed', 'Không đáp ứng yêu cầu công việc', 250, 'facebook'],
    [230, 'br-q2', 'p-front', 'voluntary', 'Lý do cá nhân / gia đình', 80, 'topcv'],
    [262, 'br-q1', 'p-tech', 'probation_fail', 'Không đạt thử việc', 25, 'facebook'],
    [300, 'br-q2', 'p-cons', 'voluntary', 'Môi trường / quản lý trực tiếp', 365, 'walkin'],
    [330, 'br-q1', 'p-doc', 'voluntary', 'Có cơ hội tốt hơn', 1300, 'referral'],
    [500, 'br-q1', 'p-cskh', 'contract_end', 'Hết hạn HĐ không gia hạn', 400, 'topcv'],
    [600, 'br-q2', 'p-tech', 'discipline', 'Vi phạm kỷ luật', 200, 'facebook', true, 'blacklist'],
  ];
  LEAVERS.forEach(([ago, branchId, positionId, type, reason, tenure, source, noRehire, tag]) => {
    specs.push({
      branchId, positionId, hire: D(-ago - tenure), source, tag,
      edu: positionId === 'p-tech' ? 'intermediate' : undefined,
      left: { date: D(-ago), type, reason, noRehire },
    });
  });

  specs.sort((a, b) => a.hire.localeCompare(b.hire));

  const employees: Employee[] = [];
  const contracts: Contract[] = [];
  const codeSeq = new Map<string, number>();
  const contractCode = (kind: Contract['kind'], start: string) => {
    const head = `${CONTRACT_PREFIX[kind]}${dayjs(start).format('YYMM')}-`;
    const n = (codeSeq.get(head) || 0) + 1;
    codeSeq.set(head, n);
    return `${head}${String(n).padStart(3, '0')}`;
  };
  const tags = new Map<string, Employee>();
  let cIdx = 0;
  const addContract = (c: Omit<Contract, 'id' | 'code' | 'createdBy' | 'createdAt'>) => {
    cIdx += 1;
    const ct: Contract = { ...c, id: `demo-c${cIdx}`, code: contractCode(c.kind, c.start), createdBy: by, createdAt: at(c.signedAt || c.start, 8) };
    contracts.push(ct);
    return ct;
  };

  specs.forEach((sp, i) => {
    const p = pos(sp.positionId);
    const dept = p.deptId;
    const gender: 'Nam' | 'Nữ' = R() < (FEMALE_RATE[dept] ?? 0.7) ? 'Nữ' : 'Nam';
    const education = sp.edu || pick(EDU[dept] || ['college']);
    const e: Employee = {
      id: `demo-e${i + 1}`, code: `NV${String(i + 1).padStart(4, '0')}`, name: makeName(gender), gender,
      dob: addDays(sp.hire, -(ri(22, p.level === 'staff' ? 30 : 38) + (dept === 'doctor' ? 4 : 0)) * 365 - ri(0, 300)), phone: makePhone(),
      branchId: sp.branchId, positionId: sp.positionId, education, hireDate: sp.hire,
      source: sp.source || pick(SOURCES[dept] || SOURCES.default),
      docs: { cccd: true, cv: true, health: true, photo: true, degree: true, license: !!p.license },
      licenses: [], ins: {}, createdAt: at(sp.hire, 8), log: [{ at: at(sp.hire, 8), by, text: 'Tạo hồ sơ nhân sự' }],
    };
    e.cccd = `0${ri(10, 99)}${ri(100000000, 999999999)}`.slice(0, 12);
    if (sp.left) Object.assign(e, { leftDate: sp.left.date, leaveType: sp.left.type, leaveReason: sp.left.reason, noRehire: sp.left.noRehire });
    if (sp.tag) tags.set(sp.tag, e);
    employees.push(e);

    /* ----- Chuỗi hợp đồng ----- */
    const official = bandSalary(p);
    const horizon = sp.left ? sp.left.date : D(30);
    if (sp.ctv) {
      let start = sp.hire;
      while (start <= horizon) {
        const end = addDays(addMonths(start, 6), -1);
        addContract({ empId: e.id, kind: 'service', start, end, salary: roundSalary(official * 0.6), signedAt: start });
        start = addDays(end, 1);
      }
      return;
    }
    const lim = probationLimit(p.level, education);
    const pEnd = probationEndOf(sp.hire, lim);
    const prob = addContract({
      empId: e.id, kind: 'probation', start: sp.hire, end: pEnd, salary: roundSalary(official * (R() < 0.6 ? 0.85 : 1)), officialSalary: official,
      signedAt: sp.unsigned ? undefined : sp.hire,
    });
    if (sp.left?.type === 'probation_fail') {
      prob.evaluation = { result: 'fail', by: 'Trưởng bộ phận', at: at(sp.left.date, 16), score: ri(4, 5), note: 'Chưa đạt yêu cầu tay nghề / doanh số trong thời gian thử việc' };
      return;
    }
    if (pEnd >= today || sp.noEval || (sp.left && sp.left.date <= pEnd)) return;
    prob.evaluation = { result: 'pass', by: 'Trưởng bộ phận', at: at(addDays(pEnd, -2), 15), score: ri(7, 9) };
    let start = addDays(pEnd, 1);
    let salary = official;
    let fixed = 0;
    while (start <= horizon) {
      if (fixed < 2) {
        const end = addDays(addMonths(start, fixed === 0 ? 12 : 24), -1);
        addContract({ empId: e.id, kind: 'fixed', start, end, salary, signedAt: addDays(start, -ri(1, 6)) });
        fixed += 1;
        start = addDays(end, 1);
        salary = roundSalary(salary * (1.05 + R() * 0.05));
      } else {
        addContract({ empId: e.id, kind: 'indefinite', start, salary, signedAt: addDays(start, -ri(1, 6)) });
        break;
      }
    }
  });

  const empContractsOf = (id: string) => contracts.filter(c => c.empId === id).sort((a, b) => a.start.localeCompare(b.start));
  /** Đặt lại ngày hết hạn cho HĐ đang hiệu lực 30 ngày trước `end` và bỏ các HĐ sau đó (HĐ chưa được gia hạn) */
  const forceEnd = (e: Employee | undefined, end: string) => {
    if (!e) return;
    const list = empContractsOf(e.id);
    const c = list.filter(x => x.start <= addDays(end, -30)).pop();
    if (!c) return;
    c.end = end;
    if (c.kind === 'indefinite') c.kind = 'fixed';
    list.filter(x => x.start > c.start).forEach(x => contracts.splice(contracts.indexOf(x), 1));
  };
  forceEnd(tags.get('q1-nurse-2fixed'), D(6));
  forceEnd(tags.get('q2-tech-expired'), D(-8));
  forceEnd(tags.get('q1-tech-auto'), D(-41));
  forceEnd(tags.get('q2-cons-expiring'), D(25));
  forceEnd(tags.get('q1-cskh-end'), D(18));

  /* ----- Bảo hiểm, giấy phép, sức khỏe, hồ sơ ----- */
  employees.forEach(e => {
    const p = pos(e.positionId);
    const off = empContractsOf(e.id).find(c => c.kind === 'fixed' || c.kind === 'indefinite');
    if (off && off.start <= D(-20) && (!e.leftDate || off.start <= e.leftDate)) {
      e.ins.increasedAt = addDays(off.start, ri(3, 12));
      if (e.leftDate) e.ins.decreasedAt = addDays(e.leftDate, ri(1, 7));
    }
    if (p.license === 'gphn') {
      const exp = D(ri(200, 1600));
      e.licenses.push({ id: `lic-${e.id}`, kind: 'gphn', number: `${ri(1000, 9999)}/HCM-GPHN`, issuedAt: addDays(exp, -5 * 365), expiresAt: exp, scope: p.deptId === 'doctor' ? 'Khám bệnh, chữa bệnh chuyên khoa thẩm mỹ' : 'Điều dưỡng' });
    }
    if (p.license === 'cert') e.licenses.push({ id: `lic-${e.id}`, kind: 'cert', number: `CC-${ri(10000, 99999)}`, issuedAt: addDays(e.hireDate, -ri(100, 900)), scope: 'Chăm sóc da, vận hành thiết bị laser' });
    e.healthCheck = daysAgo(e.hireDate) <= 330 ? addDays(e.hireDate, -ri(3, 20)) : D(-ri(20, 330));
    if (p.id === 'p-doc-lead' && e.branchId !== 'br-td') e.medicalLead = true;
  });
  function daysAgo(d: string) { return dayjs(today).diff(dayjs(d), 'day'); }

  const tdLeadLeft = tags.get('td-lead-left');
  if (tdLeadLeft) tdLeadLeft.medicalLead = true;
  const q2Lead = employees.find(e => e.branchId === 'br-q2' && e.positionId === 'p-doc-lead' && !e.leftDate);
  if (q2Lead) q2Lead.licenses[0].expiresAt = D(45);
  // Khám sức khỏe định kỳ quá hạn
  employees.filter(e => !e.leftDate && daysAgo(e.hireDate) > 500).slice(0, 5).forEach(e => { e.healthCheck = D(-ri(370, 440)); });
  // Hồ sơ của người mới còn thiếu
  const q1Tech = tags.get('q1-tech-prob');
  if (q1Tech) { q1Tech.licenses = []; q1Tech.docs = { ...q1Tech.docs, license: false, degree: false }; }
  const q2Nurse = tags.get('q2-nurse-prob');
  if (q2Nurse) { q2Nurse.licenses = []; q2Nurse.docs = { ...q2Nurse.docs, license: false }; }
  const q2Cskh = tags.get('q2-cskh-unsigned');
  if (q2Cskh) q2Cskh.docs = { cv: true, photo: false, cccd: false, health: false, degree: true };
  const lateIns = tags.get('td-late-ins');
  if (lateIns) delete lateIns.ins.increasedAt;
  const paused = tags.get('q1-nurse-paused');
  if (paused) paused.paused = { from: D(-60), to: D(120), reason: 'Nghỉ thai sản' };
  employees.filter(e => !e.leftDate && daysAgo(e.hireDate) > 600).slice(3, 6).forEach(e => { e.docs.degree = false; });

  /* ----- Quản lý trực tiếp ----- */
  const working = employees.filter(e => !e.leftDate);
  const ceo = working.find(e => e.positionId === 'p-ceo')!;
  const hrLead = working.find(e => e.positionId === 'p-hr-lead')!;
  working.forEach(e => {
    const p = pos(e.positionId);
    if (e === ceo) return;
    const bm = working.find(x => x.branchId === e.branchId && (x.positionId === 'p-bm'));
    const lead = working.find(x => x !== e && x.branchId === e.branchId && deptOfPos(x.positionId) === p.deptId && pos(x.positionId).level !== 'staff');
    e.managerId = (p.level === 'staff' ? lead?.id : undefined) || (e.positionId === 'p-bm' ? ceo.id : bm?.id) || ceo.id;
  });
  const hrName = `${hrLead.name} (TP Nhân sự)`;
  const ceoName = `${ceo.name} (Giám đốc)`;
  const bmName = (branchId: string) => {
    const bm = working.find(e => e.branchId === branchId && e.positionId === 'p-bm');
    return bm ? `${bm.name} (GĐ chi nhánh)` : hrName;
  };

  /* ----- Thủ tục nghỉ việc ----- */
  const offboardings: Offboarding[] = [];
  const ALL_TASKS: OffTask[] = ['handover', 'assets', 'accounts', 'timesheet', 'insurance', 'settlement'];
  employees.filter(e => e.leftDate && e.leftDate >= D(-400)).forEach(e => {
    const notice = e.leaveType === 'voluntary' ? addDays(e.leftDate!, -ri(30, 46)) : addDays(e.leftDate!, -ri(3, 10));
    const tasks: Offboarding['tasks'] = {};
    ALL_TASKS.filter(t => t !== 'insurance' || e.ins.increasedAt).forEach(t => { tasks[t] = { at: at(addDays(e.leftDate!, t === 'settlement' ? ri(5, 12) : ri(-2, 3)), 14), by: hrName }; });
    const o: Offboarding = {
      id: `demo-off-${e.id}`, empId: e.id, type: e.leaveType!, noticeDate: notice, lastDay: e.leftDate!, reason: e.leaveReason || '', tasks,
      status: 'done', noRehire: e.noRehire, createdBy: hrName, createdAt: at(notice, 10), doneAt: at(addDays(e.leftDate!, 12), 16),
      log: [{ at: at(notice, 10), by: hrName, text: 'Lập thủ tục nghỉ việc' }, { at: at(addDays(e.leftDate!, 12), 16), by: hrName, text: 'Hoàn tất thủ tục nghỉ việc' }],
    };
    if (e === tags.get('off-open-ins')) {
      delete o.tasks.insurance; o.status = 'open'; delete o.doneAt; o.log = o.log.slice(0, 1); delete e.ins.decreasedAt;
    }
    if (e === tags.get('off-open-settle')) {
      o.tasks = { handover: o.tasks.handover, assets: o.tasks.assets, accounts: o.tasks.accounts }; o.status = 'open'; delete o.doneAt; o.log = o.log.slice(0, 1); delete e.ins.decreasedAt;
    }
    offboardings.push(o);
  });
  const leaving = tags.get('q1-cons-leaving');
  if (leaving) {
    Object.assign(leaving, { leftDate: D(15), leaveType: 'voluntary', leaveReason: 'Có cơ hội tốt hơn' });
    offboardings.push({ id: 'demo-off-leaving', empId: leaving.id, type: 'voluntary', noticeDate: D(-15), lastDay: D(15), reason: 'Có cơ hội tốt hơn', tasks: {}, status: 'open', createdBy: hrName, createdAt: at(D(-15), 10), log: [{ at: at(D(-15), 10), by: hrName, text: 'Nhận đơn xin nghỉ việc' }] });
  }
  const ending = tags.get('q1-cskh-end');
  if (ending) {
    Object.assign(ending, { leftDate: D(18), leaveType: 'contract_end', leaveReason: 'Hết hạn HĐ không gia hạn' });
    offboardings.push({ id: 'demo-off-ending', empId: ending.id, type: 'contract_end', noticeDate: D(-5), lastDay: D(18), reason: 'Hết hạn HĐ không gia hạn', note: 'Đánh giá năm chưa đạt, hai bên thống nhất không gia hạn', tasks: {}, status: 'open', createdBy: hrName, createdAt: at(D(-5), 10), log: [{ at: at(D(-5), 10), by: hrName, text: 'Thông báo không gia hạn HĐ' }] });
  }

  enrichProfiles(employees, contracts, { today, hrName, rng: rngOf(77001) });
  // Bác sĩ Quận 1 luân chuyển sang Thủ Đức (sắp hiệu lực) trong lúc chi nhánh tuyển phụ trách chuyên môn
  const rotDoc = employees.find(e => e.branchId === 'br-q1' && e.positionId === 'p-doc' && !e.leftDate);
  if (rotDoc) {
    rotDoc.moves = [...(rotDoc.moves || []), {
      id: 'mv-rot', decisionNo: `QĐ${dayjs(D(5)).format('YYMM')}-018`, type: 'rotation', date: D(5), until: D(95), fromPositionId: 'p-doc', toPositionId: 'p-doc',
      fromBranchId: 'br-q1', toBranchId: 'br-td', reason: 'Hỗ trợ chi nhánh Thủ Đức trong thời gian tuyển bác sĩ phụ trách chuyên môn', by: ceoName, applied: false,
    }];
    rotDoc.log.push({ at: now - 2 * DAY, by: ceoName, text: `Lập quyết định luân chuyển sang Thủ Đức (hiệu lực ${dayjs(D(5)).format('DD/MM/YYYY')})` });
  }
  // Nhân sự từng điều chuyển từ Quận 2 về Quận 1
  employees.filter(e => e.branchId === 'br-q1' && ['p-cons', 'p-tech'].includes(e.positionId) && !e.leftDate && e.hireDate < D(-800)).slice(0, 2).forEach((e, i) => {
    const date = addDays(e.hireDate, 380 + i * 60);
    e.moves = [...(e.moves || []), { id: `mv-${e.id}-t`, decisionNo: `QĐ${dayjs(date).format('YYMM')}-0${21 + i}`, type: 'transfer', date, fromPositionId: e.positionId, toPositionId: e.positionId, fromBranchId: 'br-q2', toBranchId: 'br-q1', reason: 'Bổ sung nhân sự chi nhánh Quận 1', by: ceoName, applied: true }];
    e.log.push({ at: dayjs(date).hour(8).valueOf(), by: ceoName, text: 'Quyết định điều chuyển Quận 2 → Quận 1' });
    e.log.sort((a, b) => a.at - b.at);
  });

  /* ----- Tuyển dụng ----- */
  const requisitions: Requisition[] = [];
  const candidates: Candidate[] = [];
  const reqSeq = new Map<string, number>();
  const reqCode = (d: string) => {
    const head = `YC${dayjs(d).format('YYMM')}-`;
    const n = (reqSeq.get(head) || 0) + 1;
    reqSeq.set(head, n);
    return `${head}${String(n).padStart(2, '0')}`;
  };
  let rIdx = 0;
  let kIdx = 0;
  const newReq = (r: Omit<Requisition, 'id' | 'code' | 'log' | 'salaryMin' | 'salaryMax' | 'createdBy'> & Partial<Pick<Requisition, 'salaryMin' | 'salaryMax' | 'createdBy'>>) => {
    rIdx += 1;
    const p = pos(r.positionId);
    const createdBy = r.createdBy || bmName(r.branchId);
    const req: Requisition = {
      salaryMin: p.salaryMin, salaryMax: roundSalary(p.salaryMin + (p.salaryMax - p.salaryMin) * 0.7), ...r, createdBy,
      id: `demo-r${rIdx}`, code: reqCode(toDay(r.createdAt)),
      log: [{ at: r.createdAt, by: createdBy, text: 'Tạo yêu cầu tuyển dụng' }],
    };
    if (req.approvedAt) req.log.push({ at: req.approvedAt, by: req.approvedBy || ceoName, text: 'Duyệt yêu cầu tuyển dụng' });
    if (req.status === 'rejected') req.log.push({ at: req.createdAt + 2 * DAY, by: ceoName, text: `Từ chối: ${req.rejectReason}` });
    if (req.closedAt) req.log.push({ at: req.closedAt, by: hrName, text: req.closeNote || 'Đóng yêu cầu' });
    requisitions.push(req);
    return req;
  };
  const step = (stage: Stage, t: number, note?: string, score?: number): StageStep => ({ stage, at: t, by: stage === 'skill' ? 'Trưởng bộ phận' : hrName, note, score });
  const person = (dept: string) => {
    const g: 'Nam' | 'Nữ' = R() < (FEMALE_RATE[dept] ?? 0.7) ? 'Nữ' : 'Nam';
    return { name: makeName(g), gender: g, phone: makePhone() };
  };

  /** Ứng viên đã nhận việc: dựng ngược mốc thời gian từ ngày vào làm */
  const hiredCandidate = (req: Requisition, e: Employee) => {
    kIdx += 1;
    const hireT = at(e.hireDate, 8);
    const offerT = hireT - ri(5, 10) * DAY;
    const skillT = offerT - ri(2, 5) * DAY;
    const ivT = skillT - ri(2, 4) * DAY;
    const scrT = ivT - ri(1, 3) * DAY;
    const appT = scrT - ri(0, 2) * DAY - ri(1, 6) * 3600000;
    const prob = contracts.find(c => c.empId === e.id && c.kind === 'probation');
    const c: Candidate = {
      id: `demo-k${kIdx}`, name: e.name, phone: e.phone, cccd: e.cccd, gender: e.gender, dob: e.dob, education: e.education,
      experienceYears: ri(1, 6), expectedSalary: prob?.officialSalary, source: e.source || 'topcv', requisitionId: req.id,
      stage: 'hired', outcome: 'hired', appliedAt: appT, createdBy: hrName, empId: e.id,
      history: [step('applied', appT), step('screening', scrT), step('interview', ivT, 'Giao tiếp tốt, phù hợp văn hóa', ri(7, 9)), step('skill', skillT, 'Đạt yêu cầu chuyên môn', ri(7, 9)), step('offer', offerT), step('hired', hireT)],
      offer: { salary: prob?.officialSalary || bandSalary(pos(e.positionId)), startDate: e.hireDate, status: 'accepted', at: offerT, resolvedAt: offerT + ri(1, 2) * DAY },
    };
    e.candidateId = c.id; e.requisitionId = req.id;
    candidates.push(c);
    return c;
  };

  /** Ứng viên khác của một yêu cầu: dừng ở bước `reach` rồi bị loại / tự rút (hoặc còn đang xử lý) */
  const otherCandidate = (req: Requisition, appT: number, reach: number, outcome: 'rejected' | 'withdrawn' | 'open', o: { offer?: Candidate['offer']; reason?: string; source?: Source; phone?: string; name?: string } = {}) => {
    kIdx += 1;
    const dept = deptOfPos(req.positionId);
    const who = person(dept);
    const history: StageStep[] = [];
    let t = appT;
    for (let i = 0; i <= reach; i += 1) {
      if (i > 0) t += ri(1, 4) * DAY + ri(1, 5) * 3600000;
      const st = STAGES[i];
      // Bước hiện tại của ứng viên còn đang xử lý thì chưa có kết quả
      const graded = (st === 'interview' || st === 'skill') && (i < reach || outcome === 'rejected');
      const failed = outcome === 'rejected' && i === reach;
      history.push(step(st, Math.min(t, now - (reach - i + 1) * 3600000), graded ? (failed ? 'Chưa đạt' : 'Đạt') : undefined, graded ? (failed ? ri(3, 5) : ri(6, 9)) : undefined));
    }
    const stage = STAGES[reach];
    let reason = o.reason;
    if (!reason && outcome === 'rejected') reason = reach <= 1 ? pick([REJECT_REASONS[0], REJECT_REASONS[1], deptOfPos(req.positionId) === 'doctor' || deptOfPos(req.positionId) === 'nursing' ? REJECT_REASONS[2] : REJECT_REASONS[1]]) : reach === 2 ? pick([REJECT_REASONS[3], REJECT_REASONS[5], REJECT_REASONS[6]]) : REJECT_REASONS[4];
    if (!reason && outcome === 'withdrawn') reason = reach <= 2 ? pick(['Không đến phỏng vấn', 'Nhận việc nơi khác', 'Không liên lạc được']) : 'Nhận việc nơi khác';
    const c: Candidate = {
      id: `demo-k${kIdx}`, name: o.name || who.name, phone: o.phone || who.phone, gender: who.gender, education: pick(EDU[dept] || ['college']),
      experienceYears: ri(0, 6), expectedSalary: roundSalary(req.salaryMin + R() * (req.salaryMax - req.salaryMin) * 1.2),
      source: o.source || pick(SOURCES[dept] || SOURCES.default), requisitionId: req.id, stage, outcome, history, appliedAt: appT, createdBy: hrName,
      offer: o.offer,
    };
    if (outcome !== 'open') { c.closeReason = reason; c.closedAt = Math.min(now - 3600000, t + ri(1, 3) * DAY); }
    candidates.push(c);
    return c;
  };
  const reachOf = () => { const x = R(); return x < 0.35 ? 0 : x < 0.6 ? 1 : x < 0.8 ? 2 : x < 0.92 ? 3 : 4; };

  // Yêu cầu đã đóng cho người vào làm trong 13 tháng qua
  const recentHires = employees.filter(e => e.hireDate >= D(-395) && e.hireDate <= today && !contracts.some(c => c.empId === e.id && c.kind === 'service'));
  const groups = new Map<string, Employee[]>();
  recentHires.forEach(e => {
    if (e === tags.get('q1-cons-prob')) return;
    const key = e.branchId === 'br-td' && e.hireDate <= D(-135) ? `td|${e.positionId}` : e.id;
    groups.set(key, [...(groups.get(key) || []), e]);
  });
  groups.forEach(list => {
    const first = list[0];
    const lastHire = list.reduce((m, e) => (e.hireDate > m ? e.hireDate : m), first.hireDate);
    const firstHire = list.reduce((m, e) => (e.hireDate < m ? e.hireDate : m), first.hireDate);
    const approvedAt = at(addDays(firstHire, -ri(24, 40)), 10);
    const isTd = first.branchId === 'br-td' && first.hireDate <= D(-135);
    const req = newReq({
      branchId: first.branchId, positionId: first.positionId, qty: list.length,
      reason: isTd ? 'new_branch' : pick<ReqReason>(['replace', 'replace', 'expand']),
      deadline: addDays(lastHire, ri(-3, 10)), status: 'closed', createdAt: approvedAt - ri(1, 3) * DAY,
      approvedAt, approvedBy: ceoName, closedAt: at(lastHire, 17), closeNote: 'Đủ chỉ tiêu, đóng yêu cầu',
    });
    list.forEach(e => hiredCandidate(req, e));
    const lastT = at(lastHire, 8) - 5 * DAY;
    const n = list.length * ri(3, 6);
    for (let i = 0; i < n; i += 1) {
      const reach = reachOf();
      const appT = approvedAt + R() * Math.max(DAY, lastT - approvedAt - reach * 3 * DAY);
      if (reach === 4) otherCandidate(req, appT, 4, 'withdrawn', { reason: 'Từ chối offer – lương', offer: { salary: req.salaryMin, startDate: lastHire, status: 'declined', at: appT + 12 * DAY, resolvedAt: appT + 14 * DAY, declineReason: 'Đã nhận offer cao hơn' } });
      else otherCandidate(req, appT, reach, R() < 0.72 ? 'rejected' : 'withdrawn');
    }
  });
  // Một yêu cầu từng bị từ chối (vượt định biên)
  newReq({ branchId: 'br-q2', positionId: 'p-tech', qty: 2, reason: 'expand', deadline: D(-30), status: 'rejected', createdAt: at(D(-62), 10), rejectReason: 'Lượng khách Quận 2 chưa tăng, chưa mở thêm định biên' });

  /* ----- Yêu cầu đang tuyển ----- */
  const q1ConsProb = tags.get('q1-cons-prob');
  const left55 = employees.find(e => e.leftDate === D(-55));
  const r1 = newReq({ branchId: 'br-q1', positionId: 'p-cons', qty: 2, reason: 'replace', replaceEmpIds: [left55?.id, leaving?.id].filter((x): x is string => !!x), deadline: D(5), status: 'approved', createdAt: at(D(-42), 9), approvedAt: at(D(-40), 15), approvedBy: ceoName, note: 'Thay 2 tư vấn viên nghỉ, ưu tiên có kinh nghiệm bán dịch vụ thẩm mỹ' });
  if (q1ConsProb) hiredCandidate(r1, q1ConsProb);
  otherCandidate(r1, at(D(-12)), 2, 'open'); otherCandidate(r1, at(D(-10)), 2, 'open');
  otherCandidate(r1, at(D(-14)), 3, 'open');
  const r1Offer = otherCandidate(r1, at(D(-16)), 4, 'open');
  r1Offer.offer = { salary: 10_500_000, startDate: D(10), status: 'sent', at: r1Offer.history[4].at };
  otherCandidate(r1, at(D(-6)), 0, 'open'); otherCandidate(r1, at(D(-5)), 1, 'open'); otherCandidate(r1, at(D(-1)), 0, 'open');
  for (let i = 0; i < 4; i += 1) otherCandidate(r1, at(D(-ri(15, 35))), ri(0, 2), 'rejected');
  otherCandidate(r1, at(D(-20)), 2, 'withdrawn');

  const tdLeft = tags.get('td-lead-left');
  const r2 = newReq({ branchId: 'br-td', positionId: 'p-doc-lead', qty: 1, reason: 'replace', replaceEmpIds: tdLeft ? [tdLeft.id] : [], deadline: D(-3), status: 'approved', createdAt: at(D(-34), 9), approvedAt: at(D(-33), 11), approvedBy: ceoName, salaryMin: 50 * M, salaryMax: 75 * M, createdBy: hrName, note: 'Gấp: chi nhánh chưa có bác sĩ phụ trách chuyên môn' });
  const r2Doc = otherCandidate(r2, at(D(-20)), 3, 'open');
  // Bác sĩ đã khai giấy phép nhưng chưa đối chiếu bản gốc → chưa được offer
  r2Doc.license = { kind: 'gphn', number: '003456/HCM-GPHN', issuedAt: D(-1400), issuer: 'Sở Y tế TP. Hồ Chí Minh', scope: 'Khám bệnh, chữa bệnh chuyên khoa phẫu thuật thẩm mỹ' };
  otherCandidate(r2, at(D(-28)), 1, 'rejected', { reason: 'Thiếu giấy phép / chứng chỉ' });
  otherCandidate(r2, at(D(-25)), 2, 'withdrawn', { reason: 'Nhận việc nơi khác' });
  otherCandidate(r2, at(D(-2)), 0, 'open');

  const r3 = newReq({ branchId: 'br-td', positionId: 'p-nurse', qty: 1, reason: 'expand', deadline: D(20), status: 'approved', createdAt: at(D(-23), 9), approvedAt: at(D(-22), 14), approvedBy: ceoName });
  const r3Hire = otherCandidate(r3, at(D(-19)), 4, 'open', { source: 'school' });
  r3Hire.offer = { salary: 11_000_000, startDate: D(3), status: 'accepted', at: r3Hire.history[4].at, resolvedAt: r3Hire.history[4].at + DAY };
  r3Hire.license = { kind: 'gphn', number: '012874/HCM-GPHN', issuedAt: D(-700), issuer: 'Sở Y tế TP. Hồ Chí Minh', scope: 'Điều dưỡng', verifiedBy: hrName, verifiedAt: r3Hire.history[3].at };
  otherCandidate(r3, at(D(-18)), 1, 'rejected'); otherCandidate(r3, at(D(-15)), 2, 'rejected'); otherCandidate(r3, at(D(-8)), 2, 'open');

  const r4 = newReq({ branchId: 'br-q1', positionId: 'p-tech', qty: 1, reason: 'replace', replaceEmpIds: employees.filter(e => e.leftDate === D(-120)).map(e => e.id), deadline: D(18), status: 'approved', createdAt: at(D(-13), 9), approvedAt: at(D(-12), 10), approvedBy: hrName });
  otherCandidate(r4, at(D(-9)), 2, 'open'); otherCandidate(r4, at(D(-8)), 2, 'open'); otherCandidate(r4, at(D(-4)), 1, 'open');
  const r4Offer = otherCandidate(r4, at(D(-11)), 4, 'open', { source: 'referral' });
  r4Offer.offer = { salary: 12_500_000, startDate: D(7), status: 'need_approval', at: now - 5 * 3600000 };
  // Ứng viên trùng SĐT với nhân sự cũ thuộc danh sách không tái tuyển
  const black = tags.get('blacklist');
  const blackCand = black ? otherCandidate(r4, at(D(-3)), 0, 'open', { phone: black.phone, name: black.name }) : null;

  newReq({ branchId: 'br-office', positionId: 'p-mkt', qty: 1, reason: 'expand', deadline: D(30), status: 'pending', createdAt: at(D(-2), 10), createdBy: `${working.find(e => e.positionId === 'p-mkt-lead')?.name} (TP Marketing)`, note: 'Chạy quảng cáo TikTok cho chi nhánh Thủ Đức' });
  newReq({ branchId: 'br-q2', positionId: 'p-cskh', qty: 1, reason: 'expand', deadline: D(25), status: 'pending', overPlan: true, createdAt: at(D(-1), 15), note: 'Tăng ca tối thứ 7, Chủ nhật' });
  const r7 = newReq({ branchId: 'br-q2', positionId: 'p-doc', qty: 1, reason: 'expand', deadline: D(40), status: 'approved', createdAt: at(D(-6), 9), approvedAt: at(D(-5), 11), approvedBy: ceoName, salaryMin: 30 * M, salaryMax: 45 * M });
  otherCandidate(r7, at(D(-4)), 0, 'open'); otherCandidate(r7, at(D(-2)), 1, 'open');

  // Nhân sự cũ (trước 13 tháng) không gắn ứng viên — giữ nguồn tuyển ngẫu nhiên đã gán

  // Đồng ý xử lý dữ liệu cá nhân lúc ứng tuyển (một hồ sơ cũ nhập tay chưa ghi nhận để minh họa)
  candidates.forEach(c => { if (c !== blackCand) c.consent = { at: c.appliedAt, by: c.createdBy, channel: pick(CONSENT_CHANNELS) }; });

  // Lịch phỏng vấn của ứng viên đang ở vòng phỏng vấn: sắp tới, quá giờ chưa ghi kết quả, chưa hẹn
  const slot = (d: string, h: number, m: number) => dayjs(d).hour(h).minute(m).second(0).millisecond(0).valueOf();
  // Lịch sắp tới rơi vào giờ hành chính: hôm nay nếu còn kịp, không thì chiều mai
  const soon = Math.ceil((now + 2 * 3600000) / 1800000) * 1800000;
  const nextSlot = dayjs(soon).hour() >= 8 && dayjs(soon).hour() < 17 ? soon : slot(D(1), 14, 0);
  const interviewerFor = (c: Candidate) => {
    const r = requisitions.find(x => x.id === c.requisitionId);
    if (c.stage === 'interview' || !r) return hrName;
    const lead = working.find(e => e.branchId === r.branchId && deptOfPos(e.positionId) === deptOfPos(r.positionId) && POSITIONS.find(p => p.id === e.positionId)?.level !== 'staff');
    return lead?.name || 'Trưởng bộ phận';
  };
  candidates.filter(c => c.outcome === 'open' && isInterviewStage(c.stage)).forEach((c, i) => {
    const kind = i % 4;
    if (kind === 3) return;
    const r = requisitions.find(x => x.id === c.requisitionId);
    const branch = BRANCHES.find(b => b.id === r?.branchId);
    const when = kind === 0 ? nextSlot + Math.floor(i / 4) * 3600000 : kind === 1 ? slot(D(1), 9 + (i % 3), 30) : Math.max(slot(D(-1), 15, 0), c.history[c.history.length - 1].at + 3600000);
    c.interviews = [{
      id: `demo-i${i}`, stage: c.stage as 'interview' | 'skill', at: when, place: kind === 1 && c.stage === 'interview' ? 'Online (Google Meet / Zalo video)' : `${branch?.name || 'Văn phòng'}`,
      interviewer: interviewerFor(c), status: 'scheduled', by: hrName, createdAt: c.history[c.history.length - 1].at,
    }];
  });

  const plan: Record<string, number> = {};
  Object.entries(PLAN).forEach(([b, depts]) => Object.entries(depts).forEach(([d, n]) => { plan[`${b}|${d}`] = n; }));

  return {
    v: 1, seededAt: now, demoVersion: DEMO_VERSION,
    branches: BRANCHES, depts: DEPTS, positions: POSITIONS,
    employees, contracts, offboardings, requisitions, candidates, plan,
  };
}
